import { Control, Controller, useController, useWatch } from "react-hook-form";

import { useWorkspaceConnections } from "@web-app/api";
import {
  Button,
  CurrencyInput,
  ErrorMessage,
  Field,
  Input,
  Label,
  RadioBoxWithSelect,
  SelectInput,
  TextArea,
} from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import {
  OrderDeliveryType,
  OrderPriorityType,
  TransportType,
} from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import { errorMessages, getIsRequired } from "@web-app/utils/order";

import { FormValues } from "../../types";

interface OrderDetailsTabProps {
  control: Control<FormValues>;
  deliveryType: OrderDeliveryType;
  onPriorityChange: (value: OrderPriorityType) => void;
  priority: OrderPriorityType;
  setWorkspaceOptionType: (value: string) => void;
  workspaceOptionType: string;
}

interface ChecklistItem {
  isChecked: boolean;
  label: string;
  quantity: number;
}

export function OrderDetailsTab({
  control,
  deliveryType,
  onPriorityChange,
  priority,
  setWorkspaceOptionType,
  workspaceOptionType,
}: OrderDetailsTabProps) {
  const isIndividualDelivery =
    deliveryType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;
  const { field: currencyField } = useController({
    control,
    name: "currency",
  });
  // useWatch subscribes to the "currency" field and triggers a re-render
  // when it changes, ensuring CurrencyInputs inside Controller render props
  // always see the latest currency value
  const watchedCurrency = useWatch({ control, name: "currency" }) ?? "USD";

  const timezoneOptions = Object.keys(timeZoneMap).map((t) => ({
    label: t,
    value: t,
  }));

  const { data: workspaceConnections } = useWorkspaceConnections();
  const { user } = useDispatchStore();
  const currentWorkspaceName = user?.currentWorkspace?.name || "";

  return (
    <div className="space-y-3 md:space-y-4 pb-10 md:pb-5">
      <Field>
        <Label>Description</Label>
        <Controller
          name="description"
          control={control}
          rules={{
            required: getIsRequired({
              orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
              propertyName: "description",
            })
              ? errorMessages.order.description
              : false,
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <TextArea
                id="description"
                placeholder="Enter order description"
                rows={3}
                {...field}
                invalid={!!error}
              />
              {error && <ErrorMessage message={error.message} />}
            </div>
          )}
        />
      </Field>

      <Field>
        <Label>Timezone</Label>
        <Controller
          name="timezone"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <div>
              <SelectInput
                options={timezoneOptions}
                placeholder="Select Timezone"
                selected={field.value}
                onChange={field.onChange}
                invalid={!!error}
                required
              />
              {error && <ErrorMessage message={error.message} />}
            </div>
          )}
        />
      </Field>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <Field>
          <Label>Priority</Label>
          <Controller
            name="priority"
            control={control}
            render={() => (
              <Input
                type="checkbox"
                rightLabel="High Priority"
                value={(priority === OrderPriorityType.HIGH).toString()}
                onChange={(value: string) => {
                  const boolValue = value === "true";
                  onPriorityChange(
                    boolValue ? OrderPriorityType.HIGH : OrderPriorityType.LOW,
                  );
                }}
              />
            )}
          />
        </Field>
        <Field>
          <Label>External Reference ID</Label>
          <Controller
            name="externalId"
            control={control}
            render={({ field }) => (
              <Input type="text" placeholder="123" {...field} />
            )}
          />
        </Field>
        <Field className="col-span-2 md:col-span-1">
          <Label>Mode of Transportation</Label>
          <Controller
            name="vehicleType"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "vehicleType",
              })
                ? errorMessages.order.vehicleType
                : false,
            }}
            // use proper transport options
            render={({ field, fieldState: { error } }) => (
              <div>
                <SelectInput
                  options={Object.entries(TransportType).map(
                    ([key, value]) => ({
                      label: key,
                      value: value,
                    }),
                  )}
                  placeholder="Select Vehicle"
                  selected={field.value}
                  onChange={field.onChange}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </div>
            )}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field>
          <Label>Value</Label>
          <Controller
            name="value"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "value",
              })
                ? errorMessages.order.value
                : false,
            }}
            render={({ field: valueField, fieldState: { error } }) => (
              <div>
                <CurrencyInput
                  value={valueField.value}
                  onChange={valueField.onChange}
                  selectedCurrency={watchedCurrency}
                  onCurrencyChange={currencyField.onChange}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </div>
            )}
          />
        </Field>
        <Field>
          <Label>Tip</Label>
          <Controller
            name="tip"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "tip",
              })
                ? errorMessages.order.tip
                : false,
            }}
            render={({ field: tipField, fieldState: { error } }) => (
              <div>
                <CurrencyInput
                  value={tipField.value}
                  onChange={tipField.onChange}
                  selectedCurrency={watchedCurrency}
                  onCurrencyChange={currencyField.onChange}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </div>
            )}
          />
        </Field>
        <Field className="col-span-2 md:col-span-1">
          <Label>Item Count</Label>
          <Controller
            name="itemsCount"
            control={control}
            rules={{
              required: getIsRequired({
                orderType: OrderDeliveryType.ROUTE_OPT_PICKUP,
                propertyName: "itemsCount",
              })
                ? errorMessages.order.itemsCount
                : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input
                  type="number"
                  min={0}
                  placeholder="Enter number of items"
                  {...field}
                  invalid={!!error}
                />
                {error && <ErrorMessage message={error.message} />}
              </div>
            )}
          />
        </Field>
        <Field>
          <Label>Length (cm)</Label>
          <Controller
            name="length"
            control={control}
            render={({ field }) => (
              <Input type="text" placeholder="50.25" {...field} />
            )}
          />
        </Field>
        <Field>
          <Label>Width (cm)</Label>
          <Controller
            name="width"
            control={control}
            render={({ field }) => (
              <Input type="text" placeholder="30.50" {...field} />
            )}
          />
        </Field>
        <Field>
          <Label>Height (cm)</Label>
          <Controller
            name="height"
            control={control}
            render={({ field }) => (
              <Input type="text" placeholder="10.00" {...field} />
            )}
          />
        </Field>
        <Field>
          <Label>Weight (gm)</Label>
          <Controller
            name="weight"
            control={control}
            render={({ field }) => (
              <Input type="text" placeholder="500.00" {...field} />
            )}
          />
        </Field>
        <Field>
          <Controller
            name="disableSMS"
            control={control}
            render={({ field }) => (
              <Input
                type="checkbox"
                rightLabel="Disable SMS notifications"
                onChange={(value: string) => {
                  field.onChange(value === "true");
                }}
                value={field.value.toString()}
              />
            )}
          />
        </Field>
      </div>

      {workspaceConnections?.length ? (
        <Field>
          <Label>Connected Workspace Options</Label>
          <Controller
            name="connectedWorkspaceId"
            control={control}
            render={({ field }) => (
              <RadioBoxWithSelect
                value={workspaceOptionType}
                onChange={setWorkspaceOptionType}
                selectPlaceHolder={currentWorkspaceName}
                selectValue={field.value || ""}
                onSelectChange={field.onChange}
                options={[
                  {
                    id: "1",
                    label: "Create on behalf of workspace",
                    tooltip:
                      "Enter or select the workspace that issued this order.",
                    value: "createFor",
                  },
                  {
                    id: "2",
                    label: "Send to workspace",
                    tooltip:
                      "Enter or select a workspace you wish to send this order to deliver.",
                    value: "sendTo",
                  },
                ]}
                selectOptions={
                  workspaceConnections
                    ?.filter(
                      (connection) =>
                        connection.connectedWorkspace.id !==
                        user?.currentWorkspaceId,
                    )
                    .map((connection) => ({
                      label: connection.connectedWorkspace.name,
                      value: connection.connectedWorkspace.id,
                    })) || []
                }
              />
            )}
          />
        </Field>
      ) : (
        <></>
      )}

      {isIndividualDelivery && (
        <Field>
          <Label>Item Checklist</Label>
          <Controller
            name={
              deliveryType.includes("PICKUP")
                ? "pickupChecklist"
                : "dropoffChecklist"
            }
            control={control}
            render={({ field }) => (
              <div className="space-y-2">
                {field.value?.map((item: ChecklistItem, index: number) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="checkbox"
                      disabled
                      onChange={(value) => {
                        const newValue = [...field.value];
                        newValue[index] = {
                          ...newValue[index],
                          isChecked: value === "true",
                        };
                        field.onChange(newValue);
                      }}
                    />
                    <Input
                      type="text"
                      placeholder="Item name"
                      value={item.label}
                      onChange={(value) => {
                        const newValue = [...field.value];
                        newValue[index] = {
                          ...newValue[index],
                          label: value,
                        };
                        field.onChange(newValue);
                      }}
                    />
                    <Input
                      type="number"
                      min={1}
                      placeholder="Quantity"
                      value={item.quantity}
                      onChange={(value) => {
                        const newValue = [...field.value];
                        newValue[index] = {
                          ...newValue[index],
                          quantity: parseInt(value),
                        };
                        field.onChange(newValue);
                      }}
                    />
                    <Button
                      plain
                      icon="fasTrash"
                      onClick={() => {
                        const newValue = [...field.value];
                        newValue.splice(index, 1);
                        field.onChange(newValue);
                      }}
                    />
                  </div>
                ))}
                <Button
                  plain
                  size="sm"
                  onClick={() => {
                    const newValue = [
                      ...(field.value || []),
                      {
                        isChecked: false,
                        label: "",
                        quantity: 1,
                      },
                    ];
                    field.onChange(newValue);
                  }}
                >
                  + Add Item
                </Button>
              </div>
            )}
          />
        </Field>
      )}
    </div>
  );
}
